<script setup>
import {computed, onMounted, onUnmounted} from "vue";
import IconCross from "@/assets/icons/IconCross.vue";
import ButtonAccentPrimary from "@/components/Buttons/Default/ButtonAccentPrimary.vue";
import ButtonNeutralSecondary from "@/components/Buttons/Default/ButtonNeutralSecondary.vue";

const props = defineProps({
  title: {
    type: String,
    default: ""
  },
  useCrossIcon: {
    type: Boolean,
    default: false
  },
  useOverlay: {
    type: Boolean,
    default: true
  },
  useConfirm: {
    type: Boolean,
    default: true,
  },
  useCancel: {
    type: Boolean,
    default: true,
  },
  isConfirmPressed: {
    type: Boolean,
    default: false
  },
  isCancelPressed: {
    type: Boolean,
    default: false
  },
  cancelText: {
    type: String,
    default: "Отмена"
  },
  confirmText: {
    type: String,
    default: "Подтвердить"
  },
  teleport: {
    type: String,
    default: "body"
  },
  useOverlayBackground: {
    type: Boolean,
    default: true
  },
  width: {
    type: [String, Number],
    default: null,
    validator: (value) => {
      if (value === null) return true;
      if (typeof value === "number") return value >= 0;
      return /^(auto|\d+(px|rem|em|%|vh|vw))$/.test(value);
    }
  },
});

const emit = defineEmits(["close", "cancel", "confirm"]);

const computedWidth = computed(() => {
  if (!props.width) return "315px";
  if (typeof props.width === "number") {
    return `${props.width}px`;
  }
  return props.width;
});

// Функция для закрытия модального окна при клике на оверлей
const handleOverlayClick = (event) => {
  if (event.target === event.currentTarget) {
    emit("close");
  }
};

// Блокировка прокрутки фона при использовании overlay
const blockScroll = () => {
  if (props.useOverlay) {
    document.body.style.overflow = "hidden";
  }
};

const unblockScroll = () => {
  if (props.useOverlay) {
    document.body.style.overflow = "";
  }
};

// Управление прокруткой при монтировании/размонтировании компонента
onMounted(() => {
  blockScroll();
});

onUnmounted(() => {
  unblockScroll();
});
</script>

<template>
  <Teleport
    :to="props.teleport"
    :disabled="!useOverlay"
  >
    <div
      :class="[
        'modal-wrapper',
        { 'modal-wrapper--overlay': useOverlay,
          'modal-wrapper--overlay-background': useOverlayBackground}
      ]"
      @click="useOverlay ? handleOverlayClick($event) : null"
    >
      <div
        ref="modalRef"
        class="modal-globus"
        :class="{ 'modal-globus--draggable': !useOverlay }"
        :style="{ width: computedWidth}"
      >
        <div class="modal-globus-header">
          <span class="modal-globus-header__title">{{ title }}</span>
          <IconCross
            v-if="useCrossIcon"
            class="modal-globus-header__icon"
            @click="emit('close')"
          />
        </div>

        <slot />

        <div
          v-if="props.useCancel || props.useConfirm"
          class="modal-globus__footer"
        >
          <slot
            name="footer"
            :cancel="() => emit('cancel')"
            :confirm="() => emit('confirm')"
          >
            <ButtonNeutralSecondary
              v-if="props.useCancel"
              :pressed="props.isCancelPressed"
              @click="emit('cancel')"
            >
              {{ cancelText }}
            </ButtonNeutralSecondary>
            <ButtonAccentPrimary
              v-if="props.useConfirm"
              :pressed="props.isConfirmPressed"
              @click="emit('confirm')"
            >
              {{ confirmText }}
            </ButtonAccentPrimary>
          </slot>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.modal-wrapper {
  &--overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 100000;

    &-background {
      background: rgba(0, 0, 0, 0.2);
    }
  }
}

.modal-globus {
  box-sizing: border-box;
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);
  background: var(--white);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  .modal-wrapper:not(.modal-wrapper--overlay) & {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
  }

  &__footer {
    display: flex;
    gap: 10px;
    margin-top: auto;

    button {
      flex: 1;
    }
  }

  &__body {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    background: var(--background-01);
    color: var(--character-title85);
    font-size: 14px;
    font-weight: 400;
    border-radius: 10px;
    padding: 20px;
    line-height: 18px;
    flex: 1;
  }

  &-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &:not(:has(.modal-globus-header__icon)) {
      justify-content: center;
    }

    &__title {
      color: var(--character-title85);
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
    }

    &__icon {
      width: 20px;
      height: 20px;
      cursor: pointer;
      user-select: none;
      color: var(--icon-icon-secondary);
      transition: color 0.2s ease;

      &:hover {
        color: var(--icon-icon-on);
      }
    }
  }
}
</style>